import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { api } from "../lib/api";
import { MapPin, Search, ArrowRight, PoundSterling, Recycle } from "lucide-react";

const JOB_TYPES = [
  { id: "all", label: "All" },
  { id: "remove", label: "Remove" },
  { id: "clear", label: "Clear" },
];

const SUBTYPES = [
  { id: "", label: "Any sub-type" },
  { id: "house", label: "House clearance" },
  { id: "garden", label: "Garden clearance" },
  { id: "end_of_tenancy", label: "End of tenancy" },
  { id: "moving_house", label: "Moving house" },
  { id: "diy_renovation", label: "DIY & renovation" },
  { id: "commercial", label: "Commercial" },
];

export default function PriceGuide() {
  const navigate = useNavigate();
  const [jobType, setJobType] = useState("all");
  const [subtype, setSubtype] = useState("");
  const [district, setDistrict] = useState("");
  const [data, setData] = useState({ items: [], total: 0, districts: [] });
  const [page, setPage] = useState(0);
  const PAGE = 24;

  useEffect(() => {
    const params = { limit: 500 };
    if (jobType !== "all") params.job_type = jobType;
    if (subtype) params.subtype = subtype;
    if (district.trim()) params.district = district.trim().toUpperCase();
    api.get("/price-guide", { params }).then((r) => {
      setData(r.data);
      setPage(0);
    }).catch(() => setData({ items: [], total: 0, districts: [] }));
  }, [jobType, subtype, district]);

  const paged = useMemo(() => data.items.slice(page * PAGE, page * PAGE + PAGE), [data.items, page]);
  const median = useMemo(() => {
    if (!data.items.length) return null;
    const sorted = [...data.items].map((x) => x.price).sort((a, b) => a - b);
    return sorted[Math.floor(sorted.length / 2)];
  }, [data.items]);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <Badge className="bg-[#EBE8E0] text-[#1B3624] hover:bg-[#EBE8E0] uppercase text-[10px] tracking-wide">
          <PoundSterling className="w-3 h-3 mr-1 inline" /> Real prices, real jobs
        </Badge>
        <h1
          data-testid="price-guide-title"
          className="mt-3 text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium"
          style={{ fontFamily: "Outfit" }}
        >
          UK waste-clearance price guide
        </h1>
        <p className="mt-2 text-sm text-[#6B6965] max-w-2xl">
          Every price below is what a real customer paid a licensed Binshare carrier for a completed job.
          Names and addresses are anonymised — you see only the postcode district, waste type, and the price paid.
          {median && (
            <>
              {" "}Median across the current filter: <span className="text-[#1B3624] font-medium">£{median.toFixed(2)}</span>.
            </>
          )}
        </p>

        {/* Filter chips */}
        <div className="mt-6 flex flex-wrap gap-2 items-center">
          <div data-testid="pg-jobtype" className="flex p-1 bg-[#EBE8E0] rounded-full shrink-0">
            {JOB_TYPES.map((t) => (
              <button
                key={t.id}
                data-testid={`pg-jobtype-${t.id}`}
                onClick={() => setJobType(t.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                  jobType === t.id ? "bg-white shadow-sm text-[#1B3624]" : "text-[#6B6965] hover:text-[#1B3624]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <select
            data-testid="pg-subtype"
            value={subtype}
            onChange={(e) => setSubtype(e.target.value)}
            className="rounded-full border-[#E2DFD8] bg-white text-sm px-3 py-2"
          >
            {SUBTYPES.map((s) => <option key={s.id || "any"} value={s.id}>{s.label}</option>)}
          </select>
          <div className="relative flex-1 min-w-[180px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6965]" />
            <Input
              data-testid="pg-district"
              value={district}
              onChange={(e) => setDistrict(e.target.value)}
              placeholder="Postcode district — e.g. M1, LL55, SW1A"
              className="pl-10 rounded-full bg-white uppercase"
              maxLength={6}
            />
          </div>
        </div>

        {/* Suggested districts */}
        {data.districts?.length > 0 && !district && (
          <div className="mt-3 flex flex-wrap gap-1.5 items-center">
            <span className="text-[10px] uppercase tracking-widest text-[#9B9893]">Popular districts:</span>
            {data.districts.slice(0, 12).map((d) => (
              <button
                key={d}
                data-testid={`pg-district-chip-${d}`}
                onClick={() => setDistrict(d)}
                className="rounded-full border border-[#E2DFD8] bg-white text-[11px] px-2.5 py-1 hover:border-[#1B3624]"
              >
                {d}
              </button>
            ))}
          </div>
        )}

        {/* Results grid */}
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {paged.length === 0 && (
            <Card data-testid="pg-empty" className="p-10 rounded-2xl border-dashed border-[#E2DFD8] bg-transparent text-center text-[#6B6965] col-span-full">
              No completed jobs match this filter yet. Try widening the search.
            </Card>
          )}
          {paged.map((row) => (
            <Card key={row.id} data-testid={`pg-row-${row.id}`} className="p-0 rounded-2xl border-[#E2DFD8] overflow-hidden bg-white">
              {row.image_url && (
                <img src={row.image_url} alt="" className="w-full h-36 object-cover" />
              )}
              <div className="p-5">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="bg-[#EBE8E0] text-[#1B3624] hover:bg-[#EBE8E0] uppercase text-[10px]">
                    {row.waste_type || row.job_type}
                  </Badge>
                  {row.job_type === "clear" && row.job_subtype && (
                    <Badge className="bg-[#1B3624] text-white hover:bg-[#1B3624] uppercase text-[10px]">
                      Clear · {row.job_subtype.replace(/_/g, " ")}
                    </Badge>
                  )}
                </div>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-2xl font-medium text-[#1B3624]" style={{ fontFamily: "Outfit" }}>
                    £{row.price.toFixed(2)}
                  </span>
                </div>
                <div className="mt-1 text-xs text-[#6B6965] flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {row.district}
                  {row.completed_at && <span className="text-[#9B9893]"> · {row.completed_at.slice(0, 10)}</span>}
                </div>
                {/* Phase 5.1 — reuse breakdown surfaced on the completed-jobs feed.
                    Renders a compact reused/recycled/landfill split so customers
                    can see how much of the job stayed out of landfill and by whom. */}
                {row.disposal_breakdown && (
                  <div data-testid={`price-guide-reuse-${row.id}`} className="mt-3 pt-3 border-t border-[#E2DFD8]">
                    {row.carrier_company && (
                      <div className="text-[11px] text-[#6B6965] mb-1.5 truncate">by {row.carrier_company}</div>
                    )}
                    <div className="flex items-center gap-1.5 text-[11px]">
                      <Recycle className="w-3 h-3 text-[#4A6B56]" />
                      <span className="text-[#4A6B56] font-medium">{row.disposal_breakdown.reused ?? 0}% reused</span>
                      <span className="text-[#9B9893]">·</span>
                      <span className="text-[#1B3624]">{row.disposal_breakdown.recycled ?? 0}% recycled</span>
                      <span className="text-[#9B9893]">·</span>
                      <span className="text-[#6B6965]">{row.disposal_breakdown.landfilled ?? 0}% landfill</span>
                    </div>
                    {/* Diverted-from-landfill bar. Two stacked segments = reused + recycled. */}
                    <div className="mt-2 h-1.5 bg-[#EBE8E0] rounded-full overflow-hidden flex">
                      <div
                        className="h-full bg-[#4A6B56]"
                        style={{ width: `${row.disposal_breakdown.reused ?? 0}%` }}
                      />
                      <div
                        className="h-full bg-[#1B3624]"
                        style={{ width: `${row.disposal_breakdown.recycled ?? 0}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>

        {data.items.length > PAGE && (
          <div className="mt-6 flex items-center justify-between text-sm text-[#6B6965]">
            <span>Page {page + 1} of {Math.ceil(data.items.length / PAGE)}</span>
            <div className="flex gap-2">
              <button
                data-testid="pg-prev"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="rounded-full border border-[#E2DFD8] px-3 py-1.5 text-xs disabled:opacity-40"
              >
                Prev
              </button>
              <button
                data-testid="pg-next"
                onClick={() => setPage((p) => Math.min(Math.ceil(data.items.length / PAGE) - 1, p + 1))}
                disabled={(page + 1) * PAGE >= data.items.length}
                className="rounded-full border border-[#E2DFD8] px-3 py-1.5 text-xs disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}

        <Card className="mt-10 p-6 rounded-3xl bg-[#1B3624] text-white flex flex-wrap items-center gap-4 justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#D67B54]">Ready to post your own?</div>
            <div className="mt-1 text-lg font-medium" style={{ fontFamily: "Outfit" }}>
              Snap a photo. Get licensed carriers to quote.
            </div>
          </div>
          <button
            data-testid="pg-cta-post"
            onClick={() => navigate("/post-waste")}
            className="rounded-full bg-white text-[#1B3624] px-5 py-2 text-sm font-medium hover:bg-[#F6F4F0]"
          >
            Post a job <ArrowRight className="w-4 h-4 inline ml-1" />
          </button>
        </Card>
      </div>
    </Layout>
  );
}
